import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Upload, X, Loader2, ScanLine, Check, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../lib/api';

export default function ReceiptScanner({ onScanComplete, onClose }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [step, setStep] = useState('capture');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type === 'application/pdf') {
      setPreviewUrl('pdf');
    } else {
      const reader = new FileReader();
      reader.onload = (event) => setPreviewUrl(event.target.result);
      reader.readAsDataURL(file);
    }
    await processReceipt(file);
  };

  const processReceipt = async (file) => {
    setStep('processing');
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/api/ai/scan-receipt', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setExtractedData(data);
      setStep('review');
    } catch (error) {
      console.error('Error processing receipt:', error);
      setStep('capture');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="receipt-scanner-modal">
      <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-xl"><ScanLine className="w-5 h-5 text-emerald-600" /></div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Scan Receipt</h2>
              <p className="text-sm text-slate-500">Upload or capture your receipt</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-5 h-5" /></Button>
        </div>
        <div className="p-6">
          <AnimatePresence mode="wait">
            {step === 'capture' && (
              <motion.div key="capture" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <input type="file" ref={fileInputRef} onChange={handleFileSelect} accept="image/*,.pdf,application/pdf" className="hidden" />
                <button onClick={() => fileInputRef.current?.click()} className="w-full aspect-[4/3] border-2 border-dashed border-slate-200 rounded-2xl flex flex-col items-center justify-center gap-4 hover:border-emerald-500 hover:bg-emerald-50/50 transition-all cursor-pointer group" data-testid="upload-receipt-area">
                  <div className="p-4 bg-slate-100 rounded-full group-hover:bg-emerald-100 transition-colors"><Upload className="w-8 h-8 text-slate-400 group-hover:text-emerald-500 transition-colors" /></div>
                  <div className="text-center"><p className="font-medium text-slate-700">Upload Receipt</p><p className="text-sm text-slate-500 mt-1">JPG, PNG, PDF supported</p></div>
                </button>
                <div className="flex gap-3">
                  <Button variant="outline" className="flex-1" onClick={() => fileInputRef.current?.click()}><Upload className="w-4 h-4 mr-2" />Choose File</Button>
                  <Button className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500" onClick={() => { fileInputRef.current.setAttribute('capture', 'environment'); fileInputRef.current?.click(); }}><Camera className="w-4 h-4 mr-2" />Take Photo</Button>
                </div>
              </motion.div>
            )}
            {step === 'processing' && (
              <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-12 flex flex-col items-center gap-6">
                {previewUrl && (previewUrl === 'pdf' ? <div className="w-32 h-32 rounded-2xl bg-red-50 flex flex-col items-center justify-center shadow-lg"><FileText className="w-10 h-10 text-red-500" /><span className="text-xs text-red-600 font-medium mt-1">PDF</span></div> : <div className="w-32 h-32 rounded-2xl overflow-hidden shadow-lg"><img src={previewUrl} alt="Receipt" className="w-full h-full object-cover" /></div>)}
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                  <div className="text-center"><p className="font-medium text-slate-900">Processing Receipt</p><p className="text-sm text-slate-500 mt-1">Extracting details with AI...</p></div>
                </div>
              </motion.div>
            )}
            {step === 'review' && extractedData && (
              <motion.div key="review" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl"><Check className="w-5 h-5 text-emerald-600" /><p className="text-sm text-emerald-700 font-medium">Receipt scanned successfully!</p></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-xl"><p className="text-xs text-slate-500 uppercase tracking-wider">Store</p><p className="font-semibold text-slate-900 mt-1">{extractedData.store_name || 'Unknown'}</p></div>
                  <div className="p-3 bg-slate-50 rounded-xl"><p className="text-xs text-slate-500 uppercase tracking-wider">Amount</p><p className="font-semibold text-slate-900 mt-1">${extractedData.amount?.toFixed?.(2) || '0.00'}</p></div>
                  <div className="p-3 bg-slate-50 rounded-xl"><p className="text-xs text-slate-500 uppercase tracking-wider">Date</p><p className="font-semibold text-slate-900 mt-1">{extractedData.date || 'Unknown'}</p></div>
                  <div className="p-3 bg-slate-50 rounded-xl"><p className="text-xs text-slate-500 uppercase tracking-wider">Category</p><p className="font-semibold text-slate-900 mt-1 capitalize">{extractedData.category?.replace(/_/g, ' ') || 'Other'}</p></div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button variant="outline" className="flex-1" onClick={() => setStep('capture')}>Scan Again</Button>
                  <Button className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500" onClick={() => onScanComplete(extractedData)} data-testid="confirm-scan-button"><Check className="w-4 h-4 mr-2" />Save Receipt</Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </motion.div>
  );
}
